import { useRef } from 'react';
import { Button } from '../ui/button';
import { ScrollArea } from '../ui/scroll-area';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../ui/dialog';
import { FileText, Upload, File, Trash2, Loader2 } from 'lucide-react';

const ALLOWED_EXTENSIONS = ['.pdf', '.docx', '.txt', '.md', '.csv'];

const formatFileSize = (bytes) => {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
};

const KnowledgeBaseModal = ({
  showDocuments,
  setShowDocuments,
  knowledgeDocs,
  docsLoading,
  uploadingDoc,
  storageUsed,
  storageLimit,
  uploadKnowledgeDoc,
  deleteKnowledgeDoc
}) => {
  const docFileInputRef = useRef(null);

  return (
    <Dialog open={showDocuments} onOpenChange={setShowDocuments}>
      <DialogContent className="sm:max-w-lg max-h-[85vh] flex flex-col" data-testid="knowledge-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-blue-400" />
            Knowledge Base
          </DialogTitle>
          <DialogDescription>
            Upload documents for Arlo to reference in all conversations
          </DialogDescription>
        </DialogHeader>

        {/* Upload Section */}
        <div className="border-b border-border pb-4">
          <input
            ref={docFileInputRef}
            type="file"
            accept=".pdf,.docx,.txt,.md,.csv"
            onChange={(e) => e.target.files?.[0] && uploadKnowledgeDoc(e.target.files[0])}
            className="hidden"
          />
          <Button
            variant="outline"
            className="w-full"
            onClick={() => docFileInputRef.current?.click()}
            disabled={uploadingDoc}
            data-testid="upload-doc-btn"
          >
            {uploadingDoc ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Upload className="h-4 w-4 mr-2" />
            )}
            {uploadingDoc ? 'Uploading...' : 'Upload Document'}
          </Button>
          <p className="text-xs text-muted-foreground mt-2">
            Supported: {ALLOWED_EXTENSIONS.join(', ')} (Max 25MB per file)
          </p>
        </div>

        {/* Documents List */}
        <ScrollArea className="flex-1 -mx-6 px-6">
          {docsLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : knowledgeDocs.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <FileText className="h-10 w-10 mx-auto mb-3 opacity-50" />
              <p className="text-sm">No documents yet</p>
              <p className="text-xs mt-1">
                Upload documents to give Arlo knowledge about your work
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {knowledgeDocs.map((doc) => (
                <div
                  key={doc.id}
                  className="group flex items-start gap-3 p-3 rounded-lg bg-muted/50 hover:bg-muted transition-colors"
                  data-testid={`doc-${doc.id}`}
                >
                  <File className="h-4 w-4 mt-0.5 flex-shrink-0 text-blue-400" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{doc.filename}</p>
                    <p className="text-xs text-muted-foreground">
                      {formatFileSize(doc.file_size)}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                      {doc.content_preview}
                    </p>
                  </div>
                  <button
                    onClick={() => deleteKnowledgeDoc(doc.id)}
                    className="opacity-0 group-hover:opacity-100 transition-opacity p-1 hover:bg-background rounded"
                    title="Delete document"
                  >
                    <Trash2 className="h-3.5 w-3.5 text-muted-foreground hover:text-destructive" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </ScrollArea>

        {/* Footer */}
        <div className="pt-3 border-t border-border text-center">
          <p className="text-xs text-muted-foreground">
            {formatFileSize(storageUsed)} / {formatFileSize(storageLimit)} used
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default KnowledgeBaseModal;
